import { Search, X } from "lucide-react";
import { useMemo } from "react";

import { DateRangePicker } from "@/components/shared/date-range-picker";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/shared/searchable-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useRawMaterials } from "@/hooks/use-raw-materials";

interface Props {
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  startDate: string;
  endDate: string;
  onDateRangeChange: (from: string, to: string) => void;
  rawMaterialId: string;
  onRawMaterialIdChange: (id: string) => void;
  onReset: () => void;
}

export function RepackShrinkageFilterBar({
  searchQuery,
  onSearchQueryChange,
  startDate,
  endDate,
  onDateRangeChange,
  rawMaterialId,
  onRawMaterialIdChange,
  onReset,
}: Props) {
  const { data: rawMaterialsRes } = useRawMaterials({ per_page: 200 });

  const materialOptions = useMemo<SearchableSelectOption[]>(() => {
    const items = rawMaterialsRes?.items || [];
    return items.map((rm) => ({
      value: rm.id,
      label: rm.name,
      hint: rm.sku || undefined,
    }));
  }, [rawMaterialsRes]);

  const hasActiveFilters = Boolean(
    searchQuery || startDate || endDate || rawMaterialId,
  );

  return (
    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
        {/* Local text search */}
        <div className="relative w-full sm:w-60">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cari bahan baku..."
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            className="h-9 pl-8 text-xs"
          />
        </div>

        {/* Date range picker */}
        <div className="w-full sm:w-auto">
          <DateRangePicker
            from={startDate}
            to={endDate}
            onChange={onDateRangeChange}
            className="w-full sm:w-[260px] text-xs"
          />
        </div>

        {/* Raw material select */}
        <div className="w-full sm:w-64">
          <SearchableSelect
            value={rawMaterialId}
            onChange={onRawMaterialIdChange}
            options={materialOptions}
            placeholder="Semua Bahan Baku"
            emptyText="Bahan baku tidak ditemukan"
            className="h-9 text-xs"
          />
        </div>
      </div>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="h-9 gap-1 text-xs text-muted-foreground hover:text-foreground self-start sm:self-auto"
        >
          <X className="h-3.5 w-3.5" />
          Reset Filter
        </Button>
      )}
    </div>
  );
}
